'use client';

import React, { createContext, useCallback, useContext, useState } from 'react';

import { useTheme } from '@/theme/ThemeContext';

import { useBreadcrumbs } from './BreadcrumbContext';
import { FeedbackForm } from './FeedbackForm';
import { useScene } from './SceneContext';
import { captureEnv, captureRoute } from './capture';
import type { FeedbackSubmission, Target } from './ontology/types';

/** Describes the feedback controls exposed to descendants by FeedbackController. */
interface FeedbackContextValue {
  openFeedback: (options?: { target?: Target }) => void;
}

/** Stores the immutable submission context captured when feedback is opened. */
type FeedbackSnapshot = Omit<FeedbackSubmission, 'tags' | 'unselected_tags' | 'message'>;

/** Holds feedback controls for descendants rendered inside FeedbackController. */
const FeedbackContext = createContext<FeedbackContextValue | undefined>(undefined);

/** Captures point-in-time feedback context and renders the feedback dialog controller. */
export const FeedbackController: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const scene = useScene();
  const breadcrumbs = useBreadcrumbs();
  const { mode } = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [snapshot, setSnapshot] = useState<FeedbackSnapshot | null>(null);

  /** Closes the dialog and clears the snapshot captured when feedback was opened. */
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSnapshot(null);
  }, []);

  /** Captures the current feedback context and opens the feedback dialog. */
  const openFeedback = useCallback(
    (options?: { target?: Target }) => {
      const nextSnapshot: FeedbackSnapshot = {
        scene,
        breadcrumbs,
        route: captureRoute(),
        env: captureEnv(mode),
        target: options?.target ?? null,
      };

      setSnapshot(nextSnapshot);
      setIsOpen(true);
    },
    [breadcrumbs, mode, scene]
  );

  return (
    <FeedbackContext.Provider value={{ openFeedback }}>
      {children}
      {snapshot && <FeedbackForm isOpen={isOpen} snapshot={snapshot} onClose={handleClose} />}
    </FeedbackContext.Provider>
  );
};

/** Returns feedback controls and requires a surrounding FeedbackController. */
export const useFeedback = (): FeedbackContextValue => {
  const context = useContext(FeedbackContext);
  if (context === undefined) {
    throw new Error('useFeedback must be used within a FeedbackController');
  }
  return context;
};
